/**
 * OtrosIngresosFactoringSection — Iter 236.o (Carlos)
 *
 * Card idéntico a "Cuentas por Cobrar (control factoring)" pero sobre la
 * colección `fc_otros_ingresos`. Incluye CRUD inline (agregar/editar/eliminar)
 * porque estos ingresos no vienen de Alegra — los crea el usuario manualmente
 * en este mismo card.
 *
 * Una vez creado, cada Otro Ingreso aparece en la fila 43.5 del grid y puede
 * asignarse a líneas de factoring igual que las CxC.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { flujoCajaApi } from '../../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Skeleton } from '../../ui/skeleton';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../ui/dialog';
import { toast } from '../../../hooks/use-toast';
import { Coins, Pencil, Plus, Search, Trash2 } from 'lucide-react';

const formatCLP = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(v || 0);
const formatCLPShort = (v) => new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(v || 0);
const todayISO = () => new Date().toISOString().split('T')[0];

const emptyForm = () => ({
  id: null,
  folio: '',
  cliente: '',
  monto: 0,
  fecha_venc: todayISO(),
  nota: '',
});

export function OtrosIngresosFactoringSection({ lineas, onAsignacionChange }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(null);

  // Dialog form (crear/editar)
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await flujoCajaApi.otrosIngresosDisponiblesFactoring();
      setItems(res.data?.items || []);
    } catch (err) {
      toast({
        title: err?.response?.data?.detail || 'Error al cargar Otros Ingresos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = items;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((c) =>
        (c.folio || '').toLowerCase().includes(q) ||
        (c.cliente || '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => (a.fecha_venc || '').localeCompare(b.fecha_venc || ''));
  }, [items, search]);

  const total = filtered.reduce((a, c) => a + (c.monto || 0), 0);
  const totalDisp = filtered.reduce((a, c) => a + Math.max(c.disponible_para_factorizar || 0, 0), 0);

  const openNew = () => { setForm(emptyForm()); setEditing('new'); };
  const openEdit = (item) => {
    setForm({
      id: item.id,
      folio: item.folio || '',
      cliente: item.cliente || '',
      monto: item.monto || 0,
      fecha_venc: item.fecha_venc || todayISO(),
      nota: item.nota || '',
    });
    setEditing(item.id);
  };

  const save = async () => {
    if (!form.folio.trim()) return toast({ title: 'Folio/Referencia requerida', variant: 'destructive' });
    if (!form.cliente.trim()) return toast({ title: 'Cliente/Concepto requerido', variant: 'destructive' });
    if (!form.monto || Number(form.monto) <= 0) return toast({ title: 'Monto inválido', variant: 'destructive' });
    if (!form.fecha_venc) return toast({ title: 'Fecha de vencimiento requerida', variant: 'destructive' });

    setSaving(true);
    try {
      const payload = {
        folio: form.folio.trim(),
        cliente: form.cliente.trim(),
        monto: Number(form.monto),
        fecha_venc: form.fecha_venc,
        nota: form.nota || '',
        estado: 'pendiente',
      };
      if (editing === 'new') await flujoCajaApi.createOtroIngreso(payload);
      else await flujoCajaApi.updateOtroIngreso(editing, payload);
      toast({ title: editing === 'new' ? 'Otro Ingreso creado' : 'Actualizado' });
      setEditing(null);
      await load();
      if (onAsignacionChange) await onAsignacionChange();
    } catch (err) {
      toast({
        title: err?.response?.data?.detail || 'Error al guardar',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.folio}"?`)) return;
    setBusy(item.id);
    try {
      await flujoCajaApi.deleteOtroIngreso(item.id);
      toast({ title: 'Eliminado' });
      await load();
      if (onAsignacionChange) await onAsignacionChange();
    } catch (err) {
      toast({
        title: err?.response?.data?.detail || 'Error al eliminar',
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  const asignarRapido = async (item, linea) => {
    setBusy(item.id);
    try {
      await flujoCajaApi.asignacionRapidaOtroIngreso(linea.id, item.id);
      toast({ title: `Asignado a ${linea.nombre}` });
      await load();
      if (onAsignacionChange) await onAsignacionChange();
    } catch (err) {
      toast({
        title: err?.response?.data?.detail || 'Error',
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  const liberarAsig = async (asig_id) => {
    if (!window.confirm('¿Liberar esta asignación?')) return;
    setBusy('liberar-' + asig_id);
    try {
      await flujoCajaApi.liberarAsignacion(asig_id);
      toast({ title: 'Asignación liberada' });
      await load();
      if (onAsignacionChange) await onAsignacionChange();
    } catch (err) {
      toast({
        title: err?.response?.data?.detail || 'Error',
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  const colorLinea = (linea_id) => lineas.find((l) => l.id === linea_id)?.color || '#64748b';

  return (
    <Card data-testid="otros-ingresos-factoring-section" className="mt-4 border-amber-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Coins className="w-4 h-4 text-amber-700" />
          Otros Ingresos (control factoring)
          <Badge variant="outline" className="ml-1 font-mono">{filtered.length}</Badge>
          <span className="ml-auto text-xs text-muted-foreground">
            Total <span className="font-semibold text-slate-900 dark:text-slate-100 tabular-nums">{formatCLP(total)}</span>
            {' · '}
            Disp. factorizar: <span className="font-semibold text-emerald-700 tabular-nums">{formatCLP(totalDisp)}</span>
          </span>
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <div className="flex items-center gap-1 flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Buscar folio o concepto…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8"
              data-testid="input-search-otros-ingresos"
            />
          </div>
          <Button
            size="sm"
            onClick={openNew}
            data-testid="btn-add-otro-ingreso"
            className="h-8 bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Agregar Otro Ingreso
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-4"><Skeleton className="h-24" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-sm">
            Sin Otros Ingresos. Haz click en "Agregar Otro Ingreso" para crear el primero.
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-amber-50 dark:bg-amber-950/40 sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-1.5 text-left w-24">Folio/Ref</th>
                  <th className="px-2 py-1.5 text-left">Concepto/Cliente</th>
                  <th className="px-2 py-1.5 text-right">Monto</th>
                  <th className="px-2 py-1.5 text-left min-w-[180px]">Líneas asignadas</th>
                  <th className="px-2 py-1.5 text-right">Disponible</th>
                  <th className="px-2 py-1.5 text-center">Vence</th>
                  <th className="px-2 py-1.5 text-center w-64">Asignar a…</th>
                  <th className="px-2 py-1.5 text-center w-16">Acc.</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const vencida = c.fecha_venc && c.fecha_venc < todayISO();
                  const asigs = c.asignaciones || [];
                  const disp = Math.max(c.disponible_para_factorizar || 0, 0);
                  return (
                    <tr key={c.id} className="border-t hover:bg-amber-50/50 dark:hover:bg-amber-950/20" data-testid={`otro-row-${c.id}`}>
                      <td className="px-2 py-1 font-mono font-semibold">{c.folio}</td>
                      <td className="px-2 py-1 truncate max-w-[200px]" title={c.cliente}>{c.cliente}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{formatCLPShort(c.monto)}</td>
                      <td className="px-2 py-1">
                        {asigs.length === 0 ? (
                          <span className="text-muted-foreground italic">— sin asignar —</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {asigs.map((a) => (
                              <Badge
                                key={a.asig_id}
                                className="text-[10px] cursor-pointer hover:opacity-80"
                                style={{ background: colorLinea(a.linea_id), color: '#fff' }}
                                title="Clic para liberar"
                                onClick={() => liberarAsig(a.asig_id)}
                                data-testid={`badge-otro-asig-${a.asig_id}`}
                              >
                                {a.linea_nombre}: {formatCLPShort(a.monto)} ×
                              </Badge>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className={`px-2 py-1 text-right tabular-nums font-bold ${disp > 0 ? 'text-emerald-700' : 'text-muted-foreground'}`}>
                        {disp > 0 ? formatCLPShort(disp) : '✓'}
                      </td>
                      <td className={`px-2 py-1 text-center text-[11px] ${vencida ? 'text-red-700 font-semibold' : ''}`}>{c.fecha_venc}</td>
                      <td className="px-2 py-1">
                        <div className="flex items-center gap-1 justify-center flex-wrap">
                          {lineas.map((l) => {
                            const yaAsig = asigs.some((a) => a.linea_id === l.id);
                            const disabled = busy === c.id || disp <= 0 || (l.disponible || 0) <= 0;
                            return (
                              <Button
                                key={l.id}
                                size="sm"
                                variant={yaAsig ? 'default' : 'outline'}
                                className="h-6 text-[10px] px-1.5"
                                disabled={disabled}
                                onClick={() => asignarRapido(c, l)}
                                style={yaAsig ? { background: l.color } : { borderColor: l.color, color: l.color }}
                                title={disabled ? 'Sin disponible o sin cupo' : `Asignar a ${l.nombre}`}
                                data-testid={`btn-otro-rapido-${c.id}-${l.id}`}
                              >
                                {(l.nombre || '').split(' ')[0]}
                              </Button>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-2 py-1">
                        <div className="flex items-center gap-1 justify-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => openEdit(c)}
                            disabled={busy === c.id}
                            title="Editar"
                            data-testid={`btn-edit-otro-${c.id}`}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => remove(c)}
                            disabled={busy === c.id || asigs.length > 0}
                            title={asigs.length > 0 ? 'Libera las asignaciones primero' : 'Eliminar'}
                            data-testid={`btn-delete-otro-${c.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Dialog CRUD */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && !saving && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-otro-ingreso">
          <DialogHeader>
            <DialogTitle>
              {editing === 'new' ? 'Nuevo Otro Ingreso' : 'Editar Otro Ingreso'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Folio / Referencia *</Label>
              <Input
                value={form.folio}
                onChange={(e) => setForm({ ...form, folio: e.target.value })}
                placeholder="Ej: ANT-2026-04, DEV-IMP-005"
                data-testid="input-otro-folio"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Concepto / Cliente *</Label>
              <Input
                value={form.cliente}
                onChange={(e) => setForm({ ...form, cliente: e.target.value })}
                placeholder="Ej: Anticipo cliente XYZ, Devolución IVA"
                data-testid="input-otro-cliente"
              />
            </div>
            <div>
              <Label className="text-xs">Monto (CLP) *</Label>
              <Input
                type="number"
                value={form.monto}
                onChange={(e) => setForm({ ...form, monto: e.target.value })}
                data-testid="input-otro-monto"
              />
            </div>
            <div>
              <Label className="text-xs">Fecha de cobro *</Label>
              <Input
                type="date"
                value={form.fecha_venc}
                onChange={(e) => setForm({ ...form, fecha_venc: e.target.value })}
                data-testid="input-otro-fecha"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Nota</Label>
              <Input
                value={form.nota}
                onChange={(e) => setForm({ ...form, nota: e.target.value })}
                placeholder="Opcional"
                data-testid="input-otro-nota"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving} data-testid="btn-otro-cancel">Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-otro-save">
              {saving ? 'Guardando…' : editing === 'new' ? 'Crear' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
